/**
 * wardec_rules.ts — WHO may declare or surrender a SPACE guild war (space campaign B, 2026-09-30; space.md §14 gap 21).
 * BYTE-IDENTICAL client ≡ server (client/src/lib/wardec_rules.ts; verify:space-commands diffs the two). No imports.
 *
 * THE COURT DECIDES WAR. `declareGuildWar` / `surrenderGuildWar` accepted the titles `leader` / `warden`; the guild
 * court (2026-09-24) retired the warden (an old warden now sits as a council seat) and elects a Guild Master and a
 * council that decides the guild's binding acts — spend, policy, expel, recall — by MOTION, a majority of the court.
 * A war binds every member (their platforms become lawful siege targets, their pilots lawful PvP marks), so it is
 * decided the same way:
 *   · any COURT member (the Guild Master or a councillor — legacy warden / exchequer seats count) lays a `war` or
 *     `peace` motion; a plain member is refused by name;
 *   · the motion carries its proposer's yes, so a court of ONE (a master with no council) carries it in the same
 *     transaction — exactly the old immediacy; with a council the majority decides (lib/guild_rules motionVerdict);
 *   · the 500 ⓒ fee is the guild's: paid from the TREASURY when the motion passes (the court's money law — guild money
 *     moves only by motion), checked when it is laid and again when it is enacted.
 * Flag `space_commands_v1` = false restores the legacy rule (leader / warden alone, the declarer's own wallet).
 */
export const WARDEC_FLAG = 'space_commands_v1';
/** The motion kinds the space wardec lays (kept OUT of the guild window's generic composer — lib/guild_rules MOTION_KINDS). */
export const WAR_MOTION = 'war';
export const PEACE_MOTION = 'peace';
export const WARDEC_MOTION_KINDS = ['war', 'peace'] as const;

/** Titles that sit on the court: the Guild Master ('leader'), a councillor, and the retired offices read as council. */
export const WARDEC_COURT_TITLES: readonly string[] = ['leader', 'council', 'warden', 'exchequer'];

export type WardecAct = 'declare' | 'surrender';
export type WardecRoute =
  | { route: 'refuse'; reason: string }
  /** Lay a motion; `passesAtOnce` when the proposer's yes alone is a majority of the court (a court of one). */
  | { route: 'motion'; passesAtOnce: boolean }
  /** The flag is off: the pre-court rule, acted on at once. */
  | { route: 'legacy' };

export function wardecRoute(i: { flagOn: boolean; title: string | null; courtSize: number; act: WardecAct }): WardecRoute {
  if (i.title === null) return { route: 'refuse', reason: 'You are not in a guild' };
  if (!i.flagOn) {
    if (i.title === 'leader' || i.title === 'warden') return { route: 'legacy' };
    return { route: 'refuse', reason: i.act === 'declare' ? 'Only the leader or warden declares war' : 'Only the leader or warden signs a surrender' };
  }
  if (!WARDEC_COURT_TITLES.includes(i.title)) {
    return {
      route: 'refuse',
      reason: i.act === 'declare'
        ? 'Only the Guild Master and the council lay a declaration of war before the court'
        : 'Only the Guild Master and the council lay a surrender before the court',
    };
  }
  // The proposer is on the court, so the court has at least one seat; one yes of one is a majority.
  return { route: 'motion', passesAtOnce: Math.max(1, i.courtSize) <= 1 };
}
